// Top-level route map. Page components live under client/src/pages/.
// On mount it loads the public runtime config (copy/branding + region info)
// into the settings slice and keeps the document title in sync with the
// configured program/page name so the same build can be rebranded per region.
//
// Uses a data router (createBrowserRouter) rather than <BrowserRouter> so
// admin pages can block in-app navigation when there are unsaved changes
// (useBlocker requires a data router).
import { useEffect } from 'react';
import {
  createBrowserRouter, createRoutesFromElements, Route, Navigate, Outlet,
} from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import LandingContactPage from './pages/LandingContactPage.jsx';
import IntakeFormPage from './pages/IntakeFormPage.jsx';
import CheckEmailPage from './pages/CheckEmailPage.jsx';
import VerifyMagicLinkPage from './pages/VerifyMagicLinkPage.jsx';
import ThankYouPage from './pages/ThankYouPage.jsx';
import BlockedPage from './pages/BlockedPage.jsx';
import AdminLoginPage from './pages/admin/AdminLoginPage.jsx';
import AdminDashboardPage from './pages/admin/AdminDashboardPage.jsx';
import AdminCatalogBoardPage from './pages/admin/AdminCatalogBoardPage.jsx';
import AdminPricingPage from './pages/admin/AdminPricingPage.jsx';
import AdminBlacklistPage from './pages/admin/AdminBlacklistPage.jsx';
import AdminInquiriesPage from './pages/admin/AdminInquiriesPage.jsx';
import AdminInquiryDetailPage from './pages/admin/AdminInquiryDetailPage.jsx';
import AdminParticipationPage from './pages/admin/AdminParticipationPage.jsx';
import AdminSettingsPage from './pages/admin/AdminSettingsPage.jsx';
import AdminBrandingPage from './pages/admin/AdminBrandingPage.jsx';
import AdminEmailPage from './pages/admin/AdminEmailPage.jsx';
import RequireAdmin from './components/admin/RequireAdmin.jsx';
import AdminLayout from './components/admin/AdminLayout.jsx';
import SnackbarHost from './components/SnackbarHost.jsx';
import { fetchPublicConfig, selectSettings } from './store/settings-slice.js';

// Root layout route: runtime-config bootstrap + document-title sync wrapped
// around every page.
const Root = () => {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);

  useEffect(() => {
    dispatch(fetchPublicConfig());
  }, [dispatch]);

  useEffect(() => {
    if (settings.page_title) document.title = settings.page_title;
  }, [settings.page_title]);

  return (
    <>
      <Outlet />
      <SnackbarHost />
    </>
  );
};

const router = createBrowserRouter(createRoutesFromElements(
  <Route element={<Root />}>
    <Route path="/" element={<LandingContactPage />} />
    <Route path="/intake" element={<IntakeFormPage />} />
    <Route path="/check-email" element={<CheckEmailPage />} />
    <Route path="/verify/:token" element={<VerifyMagicLinkPage />} />
    <Route path="/thank-you" element={<ThankYouPage />} />
    <Route path="/blocked" element={<BlockedPage />} />
    <Route path="/admin/login" element={<AdminLoginPage />} />
    <Route
      path="/admin"
      element={(
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      )}
    >
      <Route index element={<AdminDashboardPage />} />
      <Route path="catalog" element={<AdminCatalogBoardPage />} />
      <Route path="pricing" element={<AdminPricingPage />} />
      <Route path="blacklist" element={<AdminBlacklistPage />} />
      <Route path="inquiries" element={<AdminInquiriesPage />} />
      <Route path="inquiries/:id" element={<AdminInquiryDetailPage />} />
      <Route path="participation" element={<AdminParticipationPage />} />
      <Route path="settings" element={<AdminSettingsPage />} />
      <Route path="branding" element={<AdminBrandingPage />} />
      <Route path="email" element={<AdminEmailPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Route>,
));

export default router;
